import { PALETTE, type PaletteToken } from '../tokens'

/**
 * The generated avatar (#1697 O15, `ZhGeneratedAvatar`): a mirrored five-by-five mark in one accent on one ground,
 * both drawn from the palette, decided wholly by a seed string. The same seed always gives the same avatar, so a
 * preview and the PNG saved from it match, and "Generate another" is only a new seed. No image model, no network.
 */

/** The grounds an avatar sits on: the dark surfaces of the palette. */
const GROUNDS: readonly PaletteToken[] = ['systemCharcoal', 'voidBlack', 'rustBrick']

/**
 * The accents its mark is drawn in: Dirty White alone. Colour means a state (BG §18.3), and BG §18.4 gives green,
 * amber, grey, magenta and cyan (`STATE_COLOUR`) to statuses, so an avatar beside a teammate's name never uses one.
 * Of the palette left, only Dirty White stays legible on every ground; the grounds and the mark carry the variety.
 * Still a pick from a list, so every seed draws the same mark and ground it always did.
 */
const ACCENTS: readonly PaletteToken[] = ['dirtyWhite']

/** Cells across and down. The left half and the middle column are drawn; the right half mirrors the left. */
export const AVATAR_GRID = 5

/** The drawing's side in grid half-cells: a half-cell margin all round a grid of two-half-cell squares. */
export const AVATAR_VIEWBOX = AVATAR_GRID * 2 + 2

export interface AvatarPattern {
    /** The ground's palette token. */
    ground: PaletteToken
    /** The mark's palette token. */
    accent: PaletteToken
    /** `cells[row][column]`: whether that square of the mark is drawn. */
    cells: boolean[][]
}

/** FNV-1a: a 32-bit hash of `seed`, so every seed, however short, picks its own avatar. */
function hash(seed: string): number {
    let value = 0x811c9dc5
    for (let index = 0; index < seed.length; index++) {
        value ^= seed.charCodeAt(index)
        value = Math.imul(value, 0x01000193)
    }
    return value >>> 0
}

/** Mulberry32: a small, seeded generator of numbers in [0, 1). The same seed always yields the same sequence. */
function generator(seed: number): () => number {
    let state = seed
    return () => {
        state = (state + 0x6d2b79f5) | 0
        let mixed = Math.imul(state ^ (state >>> 15), 1 | state)
        mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)
        return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296
    }
}

/** The avatar `seed` draws: its ground, its accent and which squares of its mark are filled. */
export function avatarPattern(seed: string): AvatarPattern {
    const next = generator(hash(seed))
    const pick = <T>(from: readonly T[]): T => from[Math.floor(next() * from.length)] as T
    const ground = pick(GROUNDS)
    const accent = pick(ACCENTS)
    const half = Math.ceil(AVATAR_GRID / 2)
    const cells: boolean[][] = []
    for (let row = 0; row < AVATAR_GRID; row++) {
        const left = Array.from({ length: half }, () => next() < 0.5)
        cells.push([...left, ...left.slice(0, AVATAR_GRID - half).reverse()])
    }
    // A mark with nothing drawn would be a blank square: give it its centre.
    if (!cells.some(row => row.some(Boolean))) {
        const middle = Math.floor(AVATAR_GRID / 2)
        const centre = cells[middle]
        if (centre) centre[middle] = true
    }
    return { ground, accent, cells }
}

/** The filled squares of `pattern`, as positions in the drawing's half-cell units. */
export function avatarSquares(pattern: AvatarPattern): { x: number; y: number }[] {
    return pattern.cells.flatMap((row, y) =>
        row.flatMap((filled, x) => (filled ? [{ x: 1 + x * 2, y: 1 + y * 2 }] : [])),
    )
}

/** A palette colour as its three bytes. The palette holds six-digit hex values (tokens.spec.ts keeps them so). */
function colourBytes(token: PaletteToken): [number, number, number] {
    const hex = PALETTE[token].slice(1)
    return [0, 2, 4].map(offset => Number.parseInt(hex.slice(offset, offset + 2), 16)) as [number, number, number]
}

const CRC_TABLE = (() => {
    const table = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
        let c = n
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
        table[n] = c >>> 0
    }
    return table
})()

function crc32(bytes: Uint8Array): number {
    let crc = 0xffffffff
    for (const byte of bytes) crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8)
    return (crc ^ 0xffffffff) >>> 0
}

function adler32(bytes: Uint8Array): number {
    let a = 1
    let b = 0
    for (const byte of bytes) {
        a = (a + byte) % 65521
        b = (b + a) % 65521
    }
    return ((b << 16) | a) >>> 0
}

function uint32(value: number): number[] {
    return [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff]
}

/** One PNG chunk: length, type, data and the CRC of type and data. */
function chunk(type: string, data: number[] | Uint8Array): number[] {
    const body = new Uint8Array([...type].map(letter => letter.charCodeAt(0)).concat([...data]))
    return [...uint32(data.length), ...body, ...uint32(crc32(body))]
}

/** `raw` as a zlib stream of stored (uncompressed) deflate blocks: valid everywhere, with no compressor to ship. */
function zlibStored(raw: Uint8Array): number[] {
    const out = [0x78, 0x01]
    const BLOCK = 0xffff
    for (let start = 0; start < raw.length || start === 0; start += BLOCK) {
        const block = raw.subarray(start, start + BLOCK)
        const final = start + BLOCK >= raw.length ? 1 : 0
        out.push(final, block.length & 0xff, block.length >>> 8, ~block.length & 0xff, (~block.length >>> 8) & 0xff)
        for (const byte of block) out.push(byte)
        if (final) break
    }
    return [...out, ...uint32(adler32(raw))]
}

function base64(bytes: number[]): string {
    let binary = ''
    for (let start = 0; start < bytes.length; start += 0x2000) {
        binary += String.fromCharCode(...bytes.slice(start, start + 0x2000))
    }
    return btoa(binary)
}

/**
 * `pattern` drawn as a `data:image/png;base64,…` URL of `size` pixels square, which the member avatar upload takes
 * as it is. A two-colour palette image, one bit a pixel: the ground and the accent, read from the palette tokens.
 */
export function avatarPngDataUrl(pattern: AvatarPattern, size = 240): string {
    const unit = size / AVATAR_VIEWBOX
    const rowBytes = Math.ceil(size / 8)
    const raw = new Uint8Array(size * (rowBytes + 1))
    for (let y = 0; y < size; y++) {
        const offset = y * (rowBytes + 1)
        raw[offset] = 0
        const v = Math.floor(y / unit)
        const row = pattern.cells[Math.floor((v - 1) / 2)]
        for (let x = 0; x < size; x++) {
            const u = Math.floor(x / unit)
            const inside = u >= 1 && v >= 1 && u <= AVATAR_GRID * 2 && v <= AVATAR_GRID * 2
            if (inside && row?.[Math.floor((u - 1) / 2)]) {
                const at = offset + 1 + (x >> 3)
                raw[at] = (raw[at] ?? 0) | (0x80 >> (x & 7))
            }
        }
    }
    const header = [...uint32(size), ...uint32(size), 1, 3, 0, 0, 0]
    const png = [
        0x89,
        0x50,
        0x4e,
        0x47,
        0x0d,
        0x0a,
        0x1a,
        0x0a,
        ...chunk('IHDR', header),
        ...chunk('PLTE', [...colourBytes(pattern.ground), ...colourBytes(pattern.accent)]),
        ...chunk('IDAT', zlibStored(raw)),
        ...chunk('IEND', []),
    ]
    return `data:image/png;base64,${base64(png)}`
}
